import type { Query, DehydratedState } from '@tanstack/react-query';

/**
 * Answers that came inside the server-rendered document (storefront-catalog spec §4.5).
 *
 * The feeds and the listings are live data: every screen that mounts asks for them again
 * (`LIVE_STALE_TIME`). On a page the server has just rendered that question was asked a moment ago —
 * by the server, for this very document — and asking it again on mount would drop the cars the
 * visitor is already looking at to a spinner and bring the same cars back. So the first mount after
 * such a document takes the answer it came with; every later mount asks as it always did.
 *
 * Counted on the page's own clock (`performance.now()`), not on dates: the answer carries the
 * server's time, the visitor's clock may be an hour off, and neither says how long ago this
 * document was rendered.
 */
export const SERVER_ANSWER_STANDS_MS = 5_000;

const standsUntil = new Map<string, number>();

/**
 * Whether the browser brought this document back out of its own keeping — Back, Forward. Such a
 * document is shown as it was stored, without asking the server again, so the answers inside it
 * are as old as the visit they come from: a price or a booked week may have changed since.
 */
const broughtBackByHistory = (): boolean => {
  const [navigation] = performance.getEntriesByType?.('navigation') ?? [];

  return (navigation as PerformanceNavigationTiming | undefined)?.type === 'back_forward';
};

/**
 * Called once per server-rendered document, as its answers are handed to the query cache. The
 * answers of a document brought back by history do not stand: the screen shows them and asks again
 * at once, as it does on any mount.
 */
export const keepServerAnswers = (state: DehydratedState | null | undefined): void => {
  if (!state || broughtBackByHistory()) return;

  const until = performance.now() + SERVER_ANSWER_STANDS_MS;

  state.queries.forEach((query) => standsUntil.set(query.queryHash, until));
};

/**
 * `refetchOnMount` of a live query: asked on every mount, except right after the server rendered
 * the page with this very answer.
 */
export const askUnlessServerAnswered = (query: Pick<Query, 'queryHash'>): boolean => {
  // The server itself never asks on mount: what a query holds there is what the page's loader has
  // just asked for, and a query that "would ask" is rendered as one already asking — a spinner.
  if (import.meta.env.SSR) return false;

  const until = standsUntil.get(query.queryHash);

  if (until === undefined) return true;
  if (performance.now() < until) return false;

  standsUntil.delete(query.queryHash);

  return true;
};

/** For tests: no answer of an earlier test stands. */
export const forgetServerAnswers = (): void => standsUntil.clear();
